import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import orderBy from "lodash/orderBy";
import { ArrowDown, ArrowUp, ListFilter, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import PermissionFilterDialog from "./PermissionFilterDialog";

const COLUMNS = [
  { key: "path", header: "Route", className: "tw:w-5/12" },
  { key: "method", header: "Method", className: "tw:w-1/12" },
  { key: "description", header: "Description", className: "tw:w-4/12" },
  {
    key: "is_allowed",
    header: "Enabled",
    className: "tw:w-1/12 tw:text-center",
  },
];

const FILTER_LABELS = {
  path: "Route",
  method: "Method",
  is_allowed: "Enabled",
};

const formatFilterValue = (filter, value) => {
  if (filter === "method") return value.map(m => m.toUpperCase()).join(", ");
  if (filter === "is_allowed") return value ? "Yes" : "No";
  return value;
};

const matchesFilters = (permission, filters) =>
  (!("path" in filters) || permission.path.includes(filters.path)) &&
  (!("method" in filters) || filters.method.includes(permission.method)) &&
  (!("is_allowed" in filters) || permission.is_allowed === filters.is_allowed);

const SortIcon = ({ direction }) =>
  direction === "asc" ? (
    <ArrowUp data-icon="inline-end" />
  ) : (
    <ArrowDown data-icon="inline-end" />
  );

SortIcon.propTypes = {
  direction: PropTypes.oneOf(["asc", "desc"]).isRequired,
};

const PermissionsTable = ({ permissions, onSetPermissions }) => {
  const [filters, setFilters] = useState({});
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [sort, setSort] = useState({ key: "path", direction: "asc" });

  const rows = useMemo(
    () =>
      orderBy(
        permissions.filter(p => matchesFilters(p, filters)),
        [sort.key],
        [sort.direction]
      ),
    [permissions, filters, sort]
  );

  const handleSort = key =>
    setSort(current => ({
      key,
      direction:
        current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));

  const handleToggle = (permission, isAllowed) =>
    onSetPermissions(
      permissions.map(p =>
        p.path === permission.path && p.method === permission.method
          ? { ...p, is_allowed: isAllowed }
          : p
      )
    );

  const handleApplyFilters = nextFilters => {
    setFilters(nextFilters);
    setIsFilterOpen(false);
  };

  const removeFilter = filter => setFilters(({ [filter]: _, ...rest }) => rest);

  return (
    <div className="tw:flex tw:flex-col tw:gap-2">
      <PermissionFilterDialog
        open={isFilterOpen}
        onOpenChange={setIsFilterOpen}
        filters={filters}
        onApply={handleApplyFilters}
      />
      <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
        <Button
          variant="outline"
          size="icon-sm"
          aria-label="Filter permissions"
          onClick={() => setIsFilterOpen(true)}
        >
          <ListFilter />
        </Button>
        {Object.entries(filters).map(([filter, value]) => (
          <Button
            key={filter}
            variant="outline"
            size="sm"
            onClick={() => removeFilter(filter)}
          >
            {`${FILTER_LABELS[filter]}: ${formatFilterValue(filter, value)}`}
            <X data-icon="inline-end" />
          </Button>
        ))}
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            {COLUMNS.map(column => (
              <TableHead key={column.key} className={column.className}>
                <Button
                  variant="ghost"
                  size="sm"
                  className="tw:-ml-2.5"
                  onClick={() => handleSort(column.key)}
                >
                  {column.header}
                  {sort.key === column.key && (
                    <SortIcon direction={sort.direction} />
                  )}
                </Button>
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={COLUMNS.length}
                className="tw:text-center tw:text-muted-foreground"
              >
                No permissions
              </TableCell>
            </TableRow>
          ) : (
            rows.map(permission => (
              <TableRow key={permission.path + permission.method}>
                <TableCell className="tw:whitespace-normal tw:break-all">
                  {permission.path}
                </TableCell>
                <TableCell>{permission.method.toUpperCase()}</TableCell>
                <TableCell className="tw:whitespace-normal">
                  {permission.description}
                </TableCell>
                <TableCell>
                  <div className="tw:flex tw:justify-center">
                    <Checkbox
                      aria-label={`Allow ${permission.method.toUpperCase()} ${permission.path}`}
                      checked={permission.is_allowed}
                      onCheckedChange={checked =>
                        handleToggle(permission, checked)
                      }
                    />
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
};

PermissionsTable.propTypes = {
  permissions: PropTypes.arrayOf(
    PropTypes.shape({
      path: PropTypes.string.isRequired,
      method: PropTypes.string.isRequired,
      description: PropTypes.string,
      is_allowed: PropTypes.bool.isRequired,
    })
  ).isRequired,
  onSetPermissions: PropTypes.func.isRequired,
};

export default PermissionsTable;
